import { useNavigate } from 'react-router-dom'
import { Check, ChevronRight, Footprints, HeartPulse, Moon, type LucideIcon } from 'lucide-react'
import { useAlerts, useHealthSummary } from '@/hooks'
import { avatarTint, initialsOf, highestSeverity, severityTone } from '@/components/hearth'
import { cn } from '@/lib/utils'
import type { FamilyMember, MetricType } from '@/types'

interface ParentWellbeingCardProps {
  member: FamilyMember
  familyId: string | null
}

interface WellbeingMetricSpec {
  /** Metric types that can fill this slot, in preference order */
  types: MetricType[]
  icon: LucideIcon
  format: (value: number, unit: string) => string
}

function formatSleep(value: number, unit: string): string {
  const hours = unit.toLowerCase().startsWith('min') ? value / 60 : value
  return `${Math.round(hours * 10) / 10}h sleep`
}

const WELLBEING_METRICS: WellbeingMetricSpec[] = [
  {
    types: ['STEPS'],
    icon: Footprints,
    format: (value) => `${Math.round(value).toLocaleString()} steps`,
  },
  {
    types: ['HEART_RATE_RESTING', 'HEART_RATE'],
    icon: HeartPulse,
    format: (value) => `${Math.round(value)} bpm`,
  },
  {
    types: ['SLEEP_DURATION'],
    icon: Moon,
    format: formatSleep,
  },
]

/**
 * One wellbeing card per parent on the caregiver home: avatar, name, a
 * human status line ("Doing well" / "2 things worth a look"), and up to
 * three small metric lines when health data is available.
 */
export function ParentWellbeingCard({ member, familyId }: ParentWellbeingCardProps) {
  const navigate = useNavigate()

  const { data: summary } = useHealthSummary(familyId, {
    parent_user_id: member.user.id,
    period: '7d',
  })

  const { alerts, pagination } = useAlerts(familyId, {
    status: 'UNREAD',
    member_id: member.id,
    page_size: 3,
  })

  const tint = avatarTint(member.user.full_name)
  const unreadCount = pagination?.total_count ?? alerts.length
  const tone = severityTone[highestSeverity(alerts.map((alert) => alert.severity))]

  const metricLines = WELLBEING_METRICS.flatMap((spec) => {
    for (const type of spec.types) {
      const metric = summary?.metrics?.find(
        (m) => m.metric_type === type && m.latest_value != null
      )
      if (metric) {
        return [{ icon: spec.icon, text: spec.format(metric.latest_value!, metric.unit) }]
      }
    }
    return []
  }).slice(0, 3)

  return (
    <button
      type="button"
      onClick={() => navigate(`/health/member/${member.id}`)}
      className="hearth-card hearth-press w-full p-4 text-left"
    >
      <span className="flex items-center gap-3">
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
            tint.bg,
            tint.fg
          )}
        >
          {initialsOf(member.user.full_name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-gray-900">
            {member.user.full_name}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500">
            {unreadCount > 0 ? (
              <>
                <span className={cn('h-2 w-2 shrink-0 rounded-full', tone.dot)} />
                {unreadCount === 1
                  ? '1 thing worth a look'
                  : `${unreadCount} things worth a look`}
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5 shrink-0 text-success-600" />
                Doing well
              </>
            )}
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
      </span>

      {metricLines.length > 0 ? (
        <span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {metricLines.map(({ icon: Icon, text }) => (
            <span key={text} className="flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5 text-gray-400" />
              <span className="font-rounded text-[13px] font-medium tabular-nums text-gray-600">
                {text}
              </span>
            </span>
          ))}
        </span>
      ) : (
        <span className="mt-3 block text-[13px] text-gray-400">
          Waiting for first health sync
        </span>
      )}
    </button>
  )
}
